/**
 * Avatar utility functions for consistent avatar display across the application
 */

/**
 * Get initials from a full name
 * Examples:
 * - "Adewunmi Hassan" -> "AH"
 * - "John" -> "J"
 * - "Mary Jane Watson" -> "MW" (first + last)
 * - "" -> "U" (Unknown)
 * - null/undefined -> "U"
 */
export function getInitials(fullName: string | null | undefined): string {
  if (!fullName || typeof fullName !== "string") {
    return "U";
  }

  const trimmed = fullName.trim();
  if (!trimmed) {
    return "U";
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return "U";
  }

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  const firstInitial = parts[0].charAt(0).toUpperCase();
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();

  return firstInitial + lastInitial;
}

/**
 * Get a consistent background color for an avatar based on the name
 */
export function getAvatarColor(name: string | null | undefined): string {
  const colors = [
    "#1976D2",
    "#388E3C",
    "#F57C00",
    "#7B1FA2",
    "#C62828",
    "#0097A7",
    "#5D4037",
    "#455A64",
    "#D32F2F",
    "#00796B",
  ];

  if (!name) {
    return colors[0];
  }

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

/**
 * Resolve avatar URL - only returns a URL for genuine uploaded images.
 * Trusted: Cloudinary URLs, our own server uploads, or relative paths.
 * External placeholders (pravatar.cc, randomuser.me, etc.) return null -> shows initials.
 */
export function resolveAvatarUrl(
  url: string | null | undefined,
): string | null {
  if (!url) return null;

  // Relative path - prepend API base URL
  if (url.startsWith("/")) {
    return (import.meta.env.VITE_API_URL || "http://localhost:5000") + url;
  }

  // Only trust Cloudinary and our own server
  const trustedHosts = ["res.cloudinary.com", "cloudinary.com"];
  const apiBase = import.meta.env.VITE_API_URL || "http://localhost:5000";
  if (url.startsWith(apiBase) || trustedHosts.some((h) => url.includes(h))) {
    return url;
  }

  // All other external URLs (pravatar.cc, randomuser.me, ui-avatars.com, etc.)
  // are rejected - return null so initials avatar is shown instead
  return null;
}
